import Logger from "@/utils/Logger";
import { LogSource, LogSeverity } from "@/constants/errorConstants";
import { apiPost } from "./api";

/**
 * Interface for Telemetry (User Activity) Payload
 * Matches backend TelemetryLog schema
 */
export interface TelemetryPayload {
  sessionId: string;
  userEmail: string;
  pageName: string;
  additionalData: Record<string, object | null>;
}

/**
 * Interface for Diagnostic (Error/System) Payload
 * Matches backend DiagnosticLog OpenAPI schema
 */
export interface DiagnosticPayload {
  sessionId: string;
  userEmail: string;
  componentName: string;
  errorType?: string; // Optional - not required for INFO logs
  errorCode?: number; // Optional - not required for INFO logs
  message: string;
  stackTrace?: string; // Optional - backend errors only
  severity: LogSeverity;
  source: LogSource;
  rootCause?: string; // Optional - additional context
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  errors?: string[];
}

interface LogEntryReference {
  id: string;
  status: string;
}

/**
 * 1. POST Telemetry Log
 * Logs user activity/page views
 * Failures are silent to prevent disrupting user experience
 */
const postTelemetry = async (payload: TelemetryPayload): Promise<void> => {
  try {
    await apiPost<ApiResponse<LogEntryReference[]>>("/log/telemetry", payload);
  } catch (error) {
    // Silent failure - logging should never disrupt the application
    Logger.debug(`Telemetry logging failed for page: ${payload.pageName}`, [
      "LogApi",
    ]);
  }
};

/**
 * 2. POST Diagnostic Log
 * Logs errors and system diagnostics
 * Failures are silent to prevent infinite error loops
 */
const postDiagnostic = async (payload: DiagnosticPayload): Promise<void> => {
  try {
    // Safety check 1: Prevent recursion if the diagnostic endpoint itself fails
    if (payload.message?.includes("/log/diagnostic")) {
      Logger.debug("Skipping recursive diagnostic log attempt", ["LogApi"]);
      return;
    }

    // Safety check 2: Prevent logging errors about logging endpoints
    if (
      payload.message?.includes("/log/telemetry") ||
      payload.message?.includes("/log/diagnostic")
    ) {
      Logger.debug("Skipping diagnostic log about logging endpoint failure", [
        "LogApi",
      ]);
      return;
    }

    await apiPost<ApiResponse<LogEntryReference[]>>("/log/diagnostic", payload);
  } catch (error) {
    // Silent failure - CRITICAL: never throw from diagnostic logging
    Logger.debug(
      `Diagnostic logging failed for component: ${payload.componentName}`,
      ["LogApi"],
    );
  }
};

export const LogApi = {
  postTelemetry,
  postDiagnostic,
};
